/**
 * Use shop route content.helpers.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { resolveCustomerRouteMode } from '../../customer-auth/routes'
import type { CmsPage as CmsContent } from '../../cms/types'
import type { CategoryRouteData, CatalogProduct, Route } from '../types'
import type { LoadedRouteContentData } from './runtime/route-content-loader/types'

type RouteContentSetters = {
  setPageData: (value: CmsContent | null) => void
  setPageError: (value: string) => void
  setProductData: (value: CatalogProduct | null) => void
  setProductError: (value: string) => void
  setCategoryRouteData: (value: CategoryRouteData | null) => void
  setPageLoading: (value: boolean) => void
  setProductLoading: (value: boolean) => void
}

const clearLoadedContent = (setters: RouteContentSetters) => {
  setters.setPageData(null)
  setters.setPageError('')
  setters.setProductData(null)
  setters.setProductError('')
  setters.setCategoryRouteData(null)
}

const isCartRoute = (route: Route) => route.mode === 'page' && route.slug.trim().toLowerCase() === 'cart'

const isCheckoutRoute = (route: Route) => route.mode === 'page' && route.slug.trim().toLowerCase() === 'checkout'

const isSearchRoute = (route: Route) => route.mode === 'page' && route.slug.trim().toLowerCase() === 'search'

const resolveCustomerRouteTitle = (customerRouteMode: ReturnType<typeof resolveCustomerRouteMode>) => {
  if (customerRouteMode === 'login') return 'Login cliente'
  if (customerRouteMode === 'signup') return 'Registrazione cliente'
  if (customerRouteMode === 'activate') return 'Attivazione account cliente'
  if (customerRouteMode === 'forgot-password') return 'Recupera la password'
  if (customerRouteMode === 'reset-password') return 'Reimposta la password'
  return 'Area cliente'
}

/**
 * Handle static route mode.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const handleStaticRouteMode = ({
  route,
  setters,
}: {
  route: Route
  setters: RouteContentSetters
}): boolean => {
  if (route.mode === 'home') {
    clearLoadedContent(setters)
    return true
  }

  if (route.mode === 'error') {
    clearLoadedContent(setters)
    document.title = `Errore ${route.code}`
    return true
  }

  if (isCartRoute(route)) {
    clearLoadedContent(setters)
    setters.setPageLoading(false)
    setters.setProductLoading(false)
    document.title = 'Carrello'
    return true
  }

  if (isCheckoutRoute(route)) {
    clearLoadedContent(setters)
    setters.setPageLoading(false)
    setters.setProductLoading(false)
    document.title = 'Checkout'
    return true
  }

  if (isSearchRoute(route)) {
    clearLoadedContent(setters)
    setters.setPageLoading(false)
    setters.setProductLoading(false)
    document.title = 'Ricerca prodotti'
    return true
  }

  const customerRouteMode = resolveCustomerRouteMode(route)
  if (customerRouteMode) {
    clearLoadedContent(setters)
    setters.setPageLoading(false)
    setters.setProductLoading(false)
    document.title = resolveCustomerRouteTitle(customerRouteMode)
    return true
  }

  return false
}

/**
 * Prepare dynamic route loading.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const prepareDynamicRouteLoading = ({
  route,
  setters,
}: {
  route: Route
  setters: RouteContentSetters
}) => {
  clearLoadedContent(setters)
  setters.setPageLoading(route.mode !== 'product')
  setters.setProductLoading(route.mode === 'product')
}

/**
 * Apply loaded route content.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const applyLoadedRouteContent = ({
  content,
  onProductResolved,
  setters,
}: {
  content: LoadedRouteContentData
  onProductResolved: (product: CatalogProduct) => void
  setters: RouteContentSetters
}) => {
  if (content.type === 'product') {
    setters.setProductData(content.product)
    onProductResolved(content.product)
    document.title = content.product.name
    return
  }

  if (content.type === 'page') {
    setters.setPageData(content.page)
    document.title = content.page.metaTitle || content.page.title
    return
  }

  setters.setCategoryRouteData(content.categoryData)
  document.title = content.categoryData.category.name || content.categoryData.category.slug
}

/**
 * Apply route content load error.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const applyRouteContentLoadError = ({
  error,
  route,
  setters,
}: {
  error: unknown
  route: Route
  setters: RouteContentSetters
}) => {
  const message = error instanceof Error ? error.message : 'Errore caricamento contenuto'
  if (route.mode === 'product') {
    setters.setProductError(message)
    return
  }
  setters.setPageError(message)
}
